<template>
  <div class="input-group">
    <select
      :value="filterOperator"
      class="form-select"
      style="max-width: 120px"
      @change="onFilterOperatorChange($event.target.value)"
    >
      <option value="=">{{ $t('Equal to') }}</option>
      <option value="!=">{{ $t('Different from') }}</option>
      <option value="is_null">{{ $t('Is empty') }}</option>
      <option value="is_not_null">{{ $t('Is not empty') }}</option>
    </select>
    <multiselect
      :value="currentValue"
      :options="effectiveOptions"
      :multiple="true"
      :close-on-select="false"
      :clear-on-select="true"
      :preserve-search="true"
      :preselect-first="false"
      :allow-empty="true"
      :searchable="true"
      :internal-search="false"
      :show-labels="true"
      :disabled="filterOperator === 'is_null' || filterOperator === 'is_not_null'"
      label="name"
      track-by="_id"
      :placeholder="'Select one or more options...'"
      @input="onFilterValueChange"
      @search-change="onSearchChange"
      :custom-label="getMultiselectLabel"
      :loading="isSearching && (!effectiveOptions || effectiveOptions.length === 0)"
    >
      <template #selection="{ values, option }">
        <span v-if="field.isArray">
          <div v-for="(opt, i) in filteredValues(values)" :key="i">
            <span class="multiselect__tag">
              {{ getMultiselectLabel(opt) }}
              <span
                class="multiselect__tag-icon"
                @click.stop="
                  onFilterValueChange(values.filter((o) => o && o._id !== opt._id))
                "
              ></span>
            </span>
          </div>
        </span>
        <span v-else>
          <template v-if="option">
            {{ getMultiselectLabel(option) }}
            <span
              class="multiselect__tag-icon"
              @click.stop="onFilterValueChange(null)"
            ></span>
          </template>
        </span>
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { wencesApi } from "@/api/wencesApi";

export default {
  name: "FilterFieldReference",
  components: {
    Multiselect,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },
    fieldId: {
      type: String,
      default: null,
    },
    filterOperator: {
      type: String,
      default: "=",
    },
    referenceOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internalReferenceOptions: [],
      searchTimeout: null,
      isSearching: false,
    };
  },
  computed: {
    effectiveOptions() {
      return this.internalReferenceOptions.length > 0
        ? this.internalReferenceOptions
        : this.referenceOptions;
    },
    currentValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        if (Array.isArray(this.value)) {
          return this.value;
        }
        return this.value;
      }
      return null;
    },
    computedFieldId() {
      return this.fieldId || `filter-reference-${this.field.key}`;
    },
  },
  watch: {
    value: {
      handler(newValue) {
        if (newValue) {
          this.initializeWithCurrentValue();
        }
      },
      immediate: true,
    },
    referenceOptions: {
      handler(newOptions) {
        if (newOptions.length > 0 && this.internalReferenceOptions.length === 0) {
          this.internalReferenceOptions = [...newOptions];
        }
      },
      immediate: true,
    },
  },
  methods: {
    initializeWithCurrentValue() {
      const currentValue = this.value;
      if (!currentValue) {
        return;
      }
      const optionsFromValue = [];
      if (this.field.isArray && Array.isArray(currentValue)) {
        currentValue.forEach((item) => {
          if (item) {
            optionsFromValue.push(
              typeof item === "string" ? { _id: item, name: item } : item
            );
          }
        });
      } else if (currentValue) {
        optionsFromValue.push(
          typeof currentValue === "string"
            ? { _id: currentValue, name: currentValue }
            : currentValue
        );
      }
      this.internalReferenceOptions = optionsFromValue;
    },
    getMultiselectLabel(option) {
      if (!option) return "No name";
      if (typeof option === "string") {
        const optionFound = this.effectiveOptions.find(
          (opt) => opt._id === option
        );
        if (optionFound) {
          return optionFound.name || optionFound.label || String(option);
        }
        return String(option);
      }
      if (option.name) {
        return option.name;
      }
      if (option.label) {
        return option.label;
      }
      if (option._id) {
        return String(option._id);
      }
      return "No name";
    },
    filteredValues(values) {
      if (!values) return [];
      return values.filter(
        (v) => v && (v._id || v.name || v.label || typeof v === "string")
      );
    },
    async onSearchChange(searchTerm) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      if (!searchTerm || searchTerm.trim().length < 2) {
        this.initializeWithCurrentValue();
        return;
      }
      this.searchTimeout = setTimeout(async () => {
        try {
          await this.searchReferences(searchTerm);
        } catch (error) {
          console.error("Error in search:", error);
        }
      }, 300);
    },
    async searchReferences(searchTerm) {
      if (!this.field || !this.field.ref) {
        return;
      }
      this.isSearching = true;
      try {
        const isUserRef = this.field.ref.toLowerCase() === 'user' || this.field.ref.toLowerCase() === 'users';
        let url;
        if (isUserRef) {
          const params = new URLSearchParams();
          params.append("search", searchTerm.trim());
          params.append("perPage", "20");
          url = `/api/users/all?${params.toString()}`;
        } else {
          const params = new URLSearchParams();
          params.append("textPlainVersion", searchTerm.trim());
          params.append("perPage", "20");
          if (this.field.refFilter && this.field.refFilterValue) {
            params.append(this.field.refFilter, this.field.refFilterValue);
          }
          url = `/api/gotrading/dynamic/${this.field.ref}?${params.toString()}`;
        }
        const { data } = await wencesApi.get(url);
        let validData;
        if (isUserRef) {
          validData = (data.data || data || []).filter(
            (item) => item && (item._id || item.name)
          );
        } else {
          validData = (data.items || data || []).filter(
            (item) => item && (item._id || item.name)
          );
        }
        const currentValue = this.value;
        let finalOptions = [...validData];
        if (currentValue) {
          if (this.field.isArray && Array.isArray(currentValue)) {
            currentValue.forEach((item) => {
              const itemId = typeof item === "string" ? item : item._id || item;
              const exists = finalOptions.some((opt) => opt._id === itemId);
              if (!exists && itemId) {
                finalOptions.push(
                  typeof item === "string" ? { _id: item, name: item } : item
                );
              }
            });
          } else {
            const currentId =
              typeof currentValue === "string"
                ? currentValue
                : currentValue._id || currentValue;
            const exists = finalOptions.some((opt) => opt._id === currentId);
            if (!exists && currentId) {
              finalOptions.push(
                typeof currentValue === "string"
                  ? { _id: currentValue, name: currentValue }
                  : currentValue
              );
            }
          }
        }
        this.internalReferenceOptions = finalOptions;
      } catch (error) {
        console.error(
          `Error searching references for ${this.field.key}:`,
          error
        );
        this.internalReferenceOptions = [];
      } finally {
        this.isSearching = false;
      }
    },
    onFilterOperatorChange(operator) {
      this.$emit("filter-operator-change", operator);
    },
    onFilterValueChange(newValue) {
      this.$emit("input", newValue);
    },
  },
};
</script>

<style scoped>
.input-group {
  width: 100%;
}

:deep(.multiselect-container) {
  position: relative;
  z-index: 1;
}

:deep(.multiselect.is-active) {
  z-index: 99999;
}

:deep(.multiselect__content-wrapper) {
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
  z-index: 99999 !important;
  position: absolute !important;
}

:deep(.multiselect__content) {
  max-height: 200px;
  overflow-y: auto;
}
</style>

